

import { defineComponent, ref, onMounted, onBeforeUnmount } from 'vue'

import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader'
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader.js'

import ResourceTracker from '@/utils/threeResource'

export default defineComponent({
    name: 'Model',
    props: ['data', 'isLoading'],
    emits: ['loadedevent'],
    setup (props, context) {
        // 随机一个model id
        const id: string = new Date().getTime().toString(36) + '-' + Math.random().toString(36).substr(2, 8)
        // model的container
        let container: null | HTMLElement = null
        // container的尺寸
        let containerWidth: number = 0
        let containerHeight: number = 0
        // 时间
        let clock: any = null
        // 相机
        let camera: any =  null 
        // 场景
        let scene: any = null
        // 视野角度
        let fov: number = 40
        // WebGL渲染器
        let renderer: any = null
        // 轨道控制器
        let controls: any = null
        // 动画混合器
        let mixer: any = null
        // 动画数组
        let mixerActions: any[] = []
        // requestAnimationFrame动画
        let animateId: number = 0
        // 三维物体
        let modelObject: any = null
        // 三维物体的组
        let group: any = null

        let resMgr = new ResourceTracker();
        const track = resMgr.track.bind(resMgr);


        const init = () => {
            container = document.querySelector(`#model_wrapper_${id} .model-container`) as HTMLElement
            containerWidth = container.clientWidth
            containerHeight = container.clientHeight
            clock = track(new THREE.Clock())
            // 初始化相机
            camera = track(new THREE.PerspectiveCamera(fov, containerWidth / containerHeight, 1, 1000))
            camera.position.set(0, 0, 10)
            // 初始化场景
            scene = new THREE.Scene()
            // 添加光源
            addLight()
            // 场景里添加相机
            scene.add(camera)
            // 加载模型
            loadModel()
            // 初始化渲染器
            renderer = new THREE.WebGLRenderer({antialias: true, alpha: true})
            renderer.setPixelRatio(window.devicePixelRatio)
            renderer.setSize(containerWidth, containerHeight)
            renderer.setClearAlpha(0)
            container.appendChild(renderer.domElement)
            // 初始化控制器
            controls = new OrbitControls(camera, renderer.domElement)
            // 禁止模型操作和自由转动
            if (props.data.disableRotate) {
                controls.enabled = false
            } else if (props.data.controls !== false) {
                controls.autoRotate = true
                controls.maxDistance = 16
                controls.minDistance = 5
            } else {
                controls.enableZoom = false
            }
            
            window.addEventListener('resize', onWindowResize, false)
            // 渲染模型
            animate()
        }
        // 打光
        const addLight = () => {
            const lightIntensity = props.data.lightIntensity || 0.8
            const color = props.data.color || 0xffffff
            scene.add(track(new THREE.AmbientLight(color, lightIntensity)))
            const lightPos = [[0, 1, 0], [0, -1, 0], [1, 0, -1], [-1, 0, 1], [-1, 1, -1], [1, -1, 1]]
            for (let i = 0; i < lightPos.length; i++) {
                const lightDirect = track(new THREE.DirectionalLight(color, lightIntensity))
                lightDirect.position.fromArray(lightPos[i])
                scene.add(lightDirect)
            }
        }
        // 加载模型
        const loadModel = () => {
            const isfbx = props.data.url.indexOf('.fbx') > -1
            const loader = isfbx ? new FBXLoader() : new GLTFLoader()
            if (!isfbx) {
                const dracoLoader = new DRACOLoader()
                dracoLoader.setDecoderPath('/draco/')
                loader.setDRACOLoader(dracoLoader)
            }
            group = track(new THREE.Group())
            loader.load(props.data.url, (object) => {
                modelObject = isfbx ? track(object) : track(object.scene)
                // 获取模型动画
                if (object.animations.length) {
                    mixer = new THREE.AnimationMixer(object)
                    mixerActions = object.animations
                }
                // 模型ID=1 进行定制，材质改为 MeshStandardMaterial
                if (props.data.modelId == 1) {
                  const material = modelObject.children[0].material;
                  const mater = new THREE.MeshStandardMaterial({
                    alphaMap: null,
                    map: material.map,
                    side: THREE.DoubleSide,
                    alphaTest: 0.9,
                  });
                  modelObject.children[0].material = mater;
                }
                // 模型ID=2 老虎2的骨骼与贴图坐标不在同一个位置，贴图将超出摄像机视角
                if (props.data.modelId == 2) {
                  const position = modelObject.children[0].children[26].position
                  modelObject.children[0].children[20].position.set(position.x, position.y, position.z)
                }
                group.add(modelObject)
                // 模型位置和大小设置
                const position = props.data.position
                const objectBox = new THREE.Box3().setFromObject(modelObject)
                const size = objectBox.getSize(new THREE.Vector3())
                const containerSize = containerWidth * (props.data.scaleSize || 1) / containerHeight
                const maxAxis = Math.max(size.x / containerSize, size.y, size.z / containerSize)
                const setScale = 1 / maxAxis * (10 - 180 / fov) * (props.data.scaleSize || 1)
                const scale = props.data.scale || setScale
                group.scale.multiplyScalar(scale)
                if (position) {
                    group.position.fromArray(position)
                } else {
                    const max = objectBox.max
                    const min = objectBox.min
                    group.position.set(-(max.x + min.x) * setScale / 2, -(max.y + min.y) * setScale / 2, -(max.z + min.z) * setScale / 2)
                }
                // 模型ID=2 进行定制，模型位置没有归零，同时大小过小
                if (props.data.modelId == 2) {
                  group.position.set(0.5, -2.5, -0.5)
                }
                scene.add(group)
                mixerActions && handleAni()
                context.emit('loadedevent')
            }, () => {
                
            }, () => {
                context.emit('loadedevent', true)
            })
        }
        // 渲染模型
        const animate = () => {
            animateId = requestAnimationFrame(animate)
            const delta = clock.getDelta()
            if (mixer) {
                mixer.update(delta)
            }
            controls.update()
            renderer.render(scene, camera)
        }
        // 窗口变化后，重置数据
        const onWindowResize = () => {
            containerWidth = (container as HTMLElement).clientWidth
            containerHeight = (container as HTMLElement).clientHeight
            camera.aspect = containerWidth / containerHeight
            camera.updateProjectionMatrix()
            renderer.setSize(containerWidth, containerHeight)
        }
        // 运行动画
        const handleAni = () => {
            mixerActions.forEach( (clip) => {
                mixer.clipAction( clip ).play()
            })
        }

        onMounted(() => {
            init()
            // 页面刷新模型区域出现白框不好看，在页面刷新时，改区域隐藏
            window.onbeforeunload = () => {
                if (container) {
                    container.style.display = 'none'
                }
            }
        })
        // 清除模型数据
        onBeforeUnmount(() => {
            window.removeEventListener('resize', onWindowResize, false)
            cancelAnimationFrame(animateId)          
            controls.dispose()                      
            group.remove(modelObject)
            scene.clear()  
            resMgr && resMgr.dispose()
            const gl = renderer.domElement.getContext('webgl')
            gl && gl.getExtension('WEBGL_lose_context').loseContext()
            renderer.forceContextLoss()
            renderer.dispose()
            renderer.content = null
            renderer.domElement = null
            const can = container?.querySelector('canvas')
            if (can) {
                container?.removeChild(can)
            }
            renderer = null
            camera = null
            controls = null
            clock = null
            mixer = null
            mixerActions = []
            group = null
            modelObject = null
            scene = null
        })

        return {
            id,
            handleAni
        }
    }
})
